import gsap from "gsap";
import { isMobile } from "./util";

/**
 * モーダル画像表示機能を提供する関数
 * @function modalImage
 * @requires gsap - GSAPの読み込みが必要
 * @param {
 *  object: {
 *   target: string - モーダルイベントを追加する要素(default: .boxer)
 *   container: string - モーダル画像を生成する親要素(default: body)
 *   toggleClass: string - container に付与するクラス(default: is-modal)
 *   background: string - 背景色 Hex 又は RGB/RGBA(default: rgba(0,0,0,0.8))
 *   backdropFilter: string - 背景のフィルタ(default: blur(10px))
 *   close: string - close ボタンのテキスト(default: ×)
 *   prev: string - prev ボタンのテキスト(default: <)
 *   next: string - next ボタンのテキスト(default: >)
 *   duration: number - 表示アニメーションのスピード(default: 0.3)
 *   inlineContent: boolean - ID で紐づけた DOM を表示する機能のON/OFF(default: true)
 *  }
 * } config
 * @returns {void}
 */
export default function modalImage(config = {}) {
  const {
    target = '.boxer',
    container: containerSelector = 'body',
    toggleClass = 'is-modal',
    background = 'rgba(0,0,0,0.8)',
    backdropFilter: filterProperty = 'blur(10px)',
    close = '×',
    prev = '<',
    next = '>',
    duration: time = 0.3,
    inlineContent = true
  } = config;

  const targets = document.querySelectorAll(`${target}:not([data-href])`);
  const container = document.querySelector(containerSelector);
  const swipeStart = { x: null, y: null };
  const swipeEnd = { x: null, y: null };
  let clickedElement;
  let modalWrapper;
  let modalOverlay;
  let modalClose;
  let tl;
  let modalInner;
  let galleries;
  let imageId;

  if (targets.length > 0) {
    init();
  }

  /**
   * 初期化
   * @function init
   */
  function init() {
    targets.forEach(target => {
      const href = target.getAttribute('href');
      if (href) target.dataset.href = href;
      target.removeAttribute('href');
      target.addEventListener('click', handleClick);
    });
  }

  /**
   * クリックイベントを処理
   * @function handleClick
   * @param {Event} event - クリックイベント
   */
  function handleClick(event) {
    if (event.ctrlKey || event.metaKey) return;
    event.preventDefault();
    event.stopPropagation();
    clickedElement = event.currentTarget;
    createModal();
  }

  /**
   * モーダルを作成するメソッド
   * @function cleatModal
   */
  function createModal() {
    const contentHTML = `<div class="modal-wrapper">
                          <div class="modal-overlay"></div>
                          <button class="modal-close"><span>${close}</span></button>
                        </div>`;
    container.insertAdjacentHTML('afterbegin', contentHTML);

    modalWrapper = document.querySelector('.modal-wrapper');
    modalOverlay = document.querySelector('.modal-overlay');
    modalClose = document.querySelector('.modal-close');
    gsap.set(modalWrapper, { width: '100%', height: '100%', autoAlpha: 0, backgroundColor: background, backdropFilter: filterProperty, position: 'fixed', zIndex: 999 });
    gsap.set(modalOverlay, { width: '100%', height: '100%', position: 'absolute', zIndex: -1 });

    tl = createTimeline();
    openModal();
    addCloseEvent();
    modalMain(clickedElement);
  }

  /**
   * GSAPタイムラインを作成するメソッド
   * @function createTimeline
   * @returns {gsap.core.Timeline} - 作成されたGSAPタイムライン
   */
  function createTimeline() {
    return gsap.timeline({ paused: true }).to(modalWrapper, { autoAlpha: 1, duration: time });
  }

  /**
   * モーダルを表示
   * @function openModal
   */
  function openModal() {
    tl.play();
    container.classList.add(toggleClass);
  }

  /**
   * モーダルを閉じる
   * @function closeModal
   */
  function closeModal() {
    tl.reverse();
    container.classList.remove(toggleClass);
    setTimeout(() => modalWrapper.remove(), time * 1000);
  }

  /**
   * クローズイベントを追加
   * @function addCloseEvent
   */
  function addCloseEvent() {
    modalClose.addEventListener('click', closeModal);
    modalOverlay.addEventListener('click', closeModal);
  }

  /**
   * モーダルのメインコンテンツを処理
   * @function modalMain
   * @param {HTMLElement} element - クリックされた要素(default: clickedElement)
   * @param {number} [direction=0] - アニメーションの方向
   */
  function modalMain(element = clickedElement, direction = 0) {
    modalInner = document.querySelector('.modal-inner');
    if (modalInner) modalInner.remove();
    const gallery = element.dataset.gallery;
    const href = element.dataset.href;
    const galleryArea = gallery ? modalGallery(element) : '';
    const elementAlt = element.children[0] && element.children[0].alt;

    let contentHTML = `<div class="modal-inner">
                          <div class="modal-item">`;

    if (href && href.includes('#') && inlineContent) {
      contentHTML += getInlineContent(href);
    } else {
      contentHTML += getContentHTML(href, elementAlt);
    }

    contentHTML += `  </div>
                        ${galleryArea}
                      </div>`;

    modalWrapper.insertAdjacentHTML('beforeend', contentHTML);
    modalInner = document.querySelector('.modal-inner');
    const modalImage = document.querySelector('.modal-img');

    animateImage(direction);
    gallery && addEventListeners(modalImage);
  }

  /**
   * コンテンツHTMLを生成
   * @function getContentHTML
   * @param {string} href - コンテンツのURL
   * @param {string} alt - 画像の代替テキスト
   * @returns {string} - コンテンツのHTML
   */
  function getContentHTML(href, alt) {
    if (href.includes('.pdf') || href.includes('youtube.com')) {
      return `<iframe class="modal-iframe" src="${href}"></iframe>`;
    } else if (href.includes('.mp4')) {
      return `<video class="modal-iframe" src="${href}" loop autoplay muted playsinline></video>`;
    } else {
      return `<img class="modal-img" src="${href}" alt="${alt}">`;
    }
  }

  /**
   * インラインコンテンツを取得
   * @function getInlineContent
   * @param {String} href - インラインコンテンツのIDを含むURL
   * @returns {String} - インラインコンテンツのHTML
   */
  function getInlineContent(href) {
    const cleanHref = href.replace('#', '');
    const inlineContent = document.getElementById(cleanHref);
    return inlineContent ? inlineContent.outerHTML : '';
  }

  /**
   * ギャラリーエリアを生成
   * @function modalGallery
   * @param {HTMLElement} element - クリックされた要素
   * @returns {string} - ギャラリーエリアのHTML
   */
  function modalGallery(element) {
    galleries = document.querySelectorAll(`[data-gallery="${element.dataset.gallery}"]`);
    imageId = Array.prototype.findIndex.call(galleries, image => image === element);

    if (galleries.length > 1) {
      const prevButton = imageId !== 0 ? `<button class="modal-prev"><span>${prev}</span></button>` : '';
      const nextButton = imageId !== galleries.length - 1 ? `<button class="modal-next"><span>${next}</span></button>` : '';
      return `<p class="modal-gallery"><span class="modal-btns">${prevButton}<span class="modal-count"><span>${imageId + 1}/${galleries.length}</span></span>${nextButton}</span></p>`;
    }
    return '';
  }

  /**
   * ギャラリーのイベントを処理
   * @function handleGalleryEvents
   * @param {number} direction - アニメーションの方向
   * @param {number} newImageId - 新しい画像のID
   */
  function handleGalleryEvents(direction, newImageId) {
    animateImage(direction);
    modalMain(galleries[newImageId], direction);
  }

  /**
   * イベントリスナーを追加
   * @function addEventListeners
   * @param {HTMLElement} image - イベントリスナーを追加する要素
   */
  function addEventListeners(image) {
    if (galleries.length > 1) {
      const modalPrev = document.querySelector('.modal-prev');
      const modalNext = document.querySelector('.modal-next');
      modalPrev?.addEventListener('click', () => handleGalleryEvents(-30, imageId - 1));
      modalNext?.addEventListener('click', () => handleGalleryEvents(30, imageId + 1));
      addKeyEvents(modalPrev, modalNext);
    } else {
      addKeyEvents();
    }

    if (image && isMobile) {
      image.addEventListener('touchmove', logSwipe);
      image.addEventListener('touchstart', logSwipeStart);
      image.addEventListener('touchend', logSwipeEnd);
    }
  }

  /**
   * キーイベントを追加
   * @function addKeyEvents
   * @param {HTMLElement} [prev] - 前のボタン(オプション)
   * @param {HTMLElement} [next] - 次のボタン(オプション)
   */
  function addKeyEvents(prev, next) {
    document.onkeydown = (event) => {
      const { keyCode, shiftKey } = event;

      if (keyCode === 37 && prev) {
        handleGalleryEvents(-30, imageId - 1);
      } else if (keyCode === 39 && next) {
        handleGalleryEvents(30, imageId + 1);
      } else if (keyCode === 27) {
        closeModal();
      } else if (keyCode === 9) {
        const buttons = Array.from(modalWrapper.querySelectorAll('button'));
        const focusedButton = document.activeElement;
        const focusedIndex = buttons.indexOf(focusedButton);
        event.preventDefault();
        if (shiftKey) {
          const prevIndex = focusedIndex === 0 ? buttons.length - 1 : focusedIndex - 1;
          buttons[prevIndex].focus();
        } else {
          const nextIndex =
            focusedIndex === buttons.length - 1 ? 0 : focusedIndex + 1;
          buttons[nextIndex].focus();
        }
      }
    };
  }

  /**
   * モーダル内の画像のアニメーションを設定
   * @function animateImage
   * @param {Number} direction - アニメーションの方向
   */
  function animateImage(direction) {
    gsap.from(
      modalInner, {
        x: direction,
        duration: time,
      }
    );
  }

  /**
   * スワイプをログ
   * @function logSwipe
   * @param {TouchEvent} event - タッチイベント
   */
  function logSwipe(event) {
    swipeEnd.x = event.touches[0].pageX;
    swipeEnd.y = event.touches[0].pageY;
  }

  /**
   * スワイプの開始をログ
   * @function logSwipeStart
   * @param {TouchEvent} event - タッチイベント
   */
  function logSwipeStart(event) {
    event.preventDefault();
    swipeStart.x = event.touches[0].pageX;
    swipeStart.y = event.touches[0].pageY;
  }

  /**
   * スワイプの終了をログ
   * @function logSwipeEnd
   * @param {TouchEvent} event - タッチイベント
   */
  function logSwipeEnd(event) {
    event.preventDefault();
    if (Math.abs(swipeEnd.y - swipeStart.y) > 100) {
      closeModal();
    } else {
      if (galleries.length > 1) {
        if (swipeEnd.x - swipeStart.x > 50 && imageId !== 0) {
          handleGalleryEvents(-30, imageId - 1);
        } else if (swipeEnd.x - swipeStart.x < -50 && imageId !== galleries.length - 1) {
          handleGalleryEvents(30, imageId + 1);
        }
      }
    }
  }
}
